/**
 * A row's actions behind one "more" button, on the header menu's
 * `.user-dropdown` / `.dropdown-item` primitives. The menu is positioned
 * against the viewport, so a table's scroll box cannot clip it. Escape, Tab, an
 * outside click, scrolling or resizing closes it; Escape returns focus to the button.
 */

import { forwardRef, useCallback, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ChevronDown, MoreHorizontal } from 'lucide-react';

export interface RowMenuItem {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  title?: string;
}

const ITEM = '[role="menuitem"]:not(:disabled)';

const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End'] as const;
type NavKey = (typeof NAV_KEYS)[number];

/** Arrow keys walk the enabled items, wrapping at either end; Home and End jump to them. */
function moveFocus(menu: HTMLElement | null, key: NavKey) {
  const entries = [...(menu?.querySelectorAll<HTMLButtonElement>(ITEM) ?? [])];
  const at = entries.indexOf(document.activeElement as HTMLButtonElement);
  const next = {
    ArrowDown: (at + 1) % entries.length,
    ArrowUp: (at + entries.length - 1) % entries.length,
    Home: 0,
    End: entries.length - 1,
  }[key];
  entries[next]?.focus();
}

function useMenuDismissal(
  open: boolean,
  menu: RefObject<HTMLDivElement | null>,
  button: RefObject<HTMLButtonElement | null>,
  close: (returnFocus?: boolean) => void,
) {
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>(ITEM)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      // Tab closes the menu and moves on from the trigger: focus goes back to
      // it first, so the browser's Tab starts there, not from a removed item.
      if (e.key === 'Tab') close();
      if ((NAV_KEYS as readonly string[]).includes(e.key)) {
        e.preventDefault();
        moveFocus(menu.current, e.key as NavKey);
      }
    };
    const onPointer = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) close(false);
    };
    const onMove = () => close(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, menu, button, close]);
}

export interface MenuTrigger {
  text: string;
  icon?: ReactNode;
  className?: string;
}

interface Position {
  top: number;
  right: number;
}

export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  return <MenuButton label={label} items={items} />;
}

const MenuList = forwardRef<
  HTMLDivElement,
  { id: string; label: string; items: RowMenuItem[]; position: Position; close: (returnFocus?: boolean) => void }
>(function MenuList({ id, label, items, position, close }, ref) {
  return (
    <div
      ref={ref}
      id={id}
      className="user-dropdown row-menu"
      role="menu"
      aria-label={label}
      style={{ position: 'fixed', top: position.top, right: position.right }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          className="dropdown-item"
          role="menuitem"
          disabled={item.disabled}
          title={item.title}
          onClick={() => {
            close(false);
            item.onSelect();
          }}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
});

function TriggerBody({ trigger }: { trigger?: MenuTrigger }) {
  if (!trigger) return <MoreHorizontal aria-hidden="true" />;
  return (
    <>
      {trigger.icon}
      {trigger.text}
      <ChevronDown aria-hidden="true" />
    </>
  );
}

/**
 * The same menu behind a labelled button (`trigger`: "Add asset", "More
 * actions") instead of the icon-only "more" button. The labelled trigger sits
 * in a `span.menu-button` that takes `className`, so a toolbar can place it.
 */
export function MenuButton({
  label,
  items,
  trigger,
  className,
}: {
  label: string;
  items: RowMenuItem[];
  trigger?: MenuTrigger;
  className?: string;
}) {
  const [position, setPosition] = useState<Position | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const open = position !== null;

  const close = useCallback((returnFocus = true) => {
    setPosition(null);
    if (returnFocus) button.current?.focus();
  }, []);

  useMenuDismissal(open, menu, button, close);

  if (items.length === 0) return null;

  const content = (
    <>
      <button
        ref={button}
        type="button"
        className={trigger ? `btn ${trigger.className ?? 'btn-secondary'}` : 'btn btn-secondary btn-icon'}
        aria-label={trigger ? undefined : label}
        title={trigger ? undefined : 'Actions'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (open) return close();
          const rect = button.current!.getBoundingClientRect();
          setPosition({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
        }}
      >
        <TriggerBody trigger={trigger} />
      </button>
      {open && <MenuList ref={menu} id={menuId} label={label} items={items} position={position} close={close} />}
    </>
  );
  if (!trigger) return content;
  return <span className={`menu-button${className ? ` ${className}` : ''}`}>{content}</span>;
}
